import { Card, Field, Form, NumberInput, Text, H2 } from '@payaca/ambrosia';
import { FC, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { solarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';
import {
  solarTariffErrorMessage,
  solarTariffSchemas,
  type SolarTariffKind,
} from '@payaca/helpers/solarTariffs';
import { type FieldValidationResult } from '@payaca/types/fieldValidationTypes';

import useUpdateAccountSolarSettings from '@/api/mutations/account/useUpdateAccountSolarSettings';
import { useRegion } from '@/api/rest/users/useRegion';
import useGetMyAccountSolarSettings from '@/api/queries/me/useGetMyAccountSolarSettings';
import { type AccountSolarSettingsFieldsFragment } from '@/gql/graphql';
import SettingsCardPlaceholder from './SettingsCardPlaceholder';
import SettingsSaveButton from './SettingsSaveButton';
import { useBusinessAccountForm } from './useBusinessAccountForm';

type FormState = {
  solarRetailTariffMinorPerKwh: number | null;
  solarExportTariffMinorPerKwh: number | null;
};

const formatRate = (value: number): string =>
  Number.isInteger(value) ? String(value) : String(value);

const validationState = (result: FieldValidationResult | undefined) =>
  result?.isValid === false
    ? { isValid: false, validationMessages: result.errors }
    : undefined;

type Props = {
  readOnly: boolean;
};

/**
 * Per-account retail and export rates for Glow savings. Blank uses the
 * regional default. Shown only when the solar design tool is on.
 */
const CompanySettingsSolarTariffs: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const { solarSettings, isError } = useGetMyAccountSolarSettings();

  if (!solarSettings) {
    return <SettingsCardPlaceholder isError={isError} />;
  }

  return (
    <SolarTariffsForm
      key={JSON.stringify([
        solarSettings.retailTariffMinorPerKwh,
        solarSettings.exportTariffMinorPerKwh,
      ])}
      readOnly={readOnly}
      settings={solarSettings}
    />
  );
};

const SolarTariffsForm: FC<
  Props & { settings: AccountSolarSettingsFieldsFragment }
> = ({ readOnly, settings }) => {
  const methods = useForm();
  const { mutateAsync: updateAccountSolarSettings } =
    useUpdateAccountSolarSettings();
  const region = solarSavingsRegion(useRegion());
  const unit = region === 'us' ? '¢/kWh' : 'p/kWh';

  const initialState: FormState = {
    solarRetailTariffMinorPerKwh: settings.retailTariffMinorPerKwh ?? null,
    solarExportTariffMinorPerKwh: settings.exportTariffMinorPerKwh ?? null,
  };

  const validator = (kind: SolarTariffKind) => [
    {
      validate: (value: number | null) =>
        value == null || solarTariffSchemas[kind].safeParse(value).success,
      error: solarTariffErrorMessage(kind, region),
    },
  ];

  const save = useCallback(
    async (state: FormState) => {
      await updateAccountSolarSettings({
        retailTariffMinorPerKwh: state.solarRetailTariffMinorPerKwh,
        exportTariffMinorPerKwh: state.solarExportTariffMinorPerKwh,
      });
    },
    [updateAccountSolarSettings]
  );

  const {
    formState,
    formValidationResult,
    updateFormFields,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(
    initialState,
    {
      solarRetailTariffMinorPerKwh: validator('retail'),
      solarExportTariffMinorPerKwh: validator('export'),
    },
    save
  );

  return (
    <Card sizeVariant="lg">
      <Card.Body>
        <Form
          methods={methods}
          onSubmit={() => undefined}
          className="flex flex-col gap-4"
        >
          <div>
            <H2>Solar rates</H2>
            <Text>
              Used on solar estimates for bill savings and payback. Leave a
              field blank to use the regional default (
              {formatRate(settings.defaultRetailTariffMinorPerKwh)}
              {unit} retail,{' '}
              {formatRate(settings.defaultExportTariffMinorPerKwh)}
              {unit} export).
            </Text>
          </div>
          <Field.Legacy
            validationState={validationState(
              formValidationResult.solarRetailTariffMinorPerKwh
            )}
          >
            <Field.Label>Retail electricity rate ({unit})</Field.Label>
            <NumberInput
              step="any"
              value={formState.solarRetailTariffMinorPerKwh}
              disabled={readOnly}
              aria-invalid={
                formValidationResult.solarRetailTariffMinorPerKwh?.isValid ===
                false
              }
              onChange={(solarRetailTariffMinorPerKwh) =>
                updateFormFields({ solarRetailTariffMinorPerKwh })
              }
            />
          </Field.Legacy>
          <Field.Legacy
            validationState={validationState(
              formValidationResult.solarExportTariffMinorPerKwh
            )}
          >
            <Field.Label>Export rate ({unit})</Field.Label>
            <NumberInput
              step="any"
              value={formState.solarExportTariffMinorPerKwh}
              disabled={readOnly}
              aria-invalid={
                formValidationResult.solarExportTariffMinorPerKwh?.isValid ===
                false
              }
              onChange={(solarExportTariffMinorPerKwh) =>
                updateFormFields({ solarExportTariffMinorPerKwh })
              }
            />
          </Field.Legacy>
          {!readOnly && (
            <SettingsSaveButton
              label="Save rates"
              isDirty={isDirty}
              isSaving={isSaving}
              isValid={isValid}
              onSave={saveAndFlush}
            />
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsSolarTariffs;
